// Admin Plans CRUD (iter 120) — list/edit/add/delete subscription plans.
// Renders inside AdminDashboard's "Plans" tab.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Trash2, Edit3, Save, X, IndianRupee, Clock, Brain, Heart } from "lucide-react";
import CountdownPill from "@/components/poshan/CountdownPill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter126 — datetime helpers for the <input type="datetime-local"> field.
// The browser surfaces local wall-clock time; we persist a canonical ISO/UTC
// string so the countdown stays accurate across timezones.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // YYYY-MM-DDTHH:MM in LOCAL time, padded
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(localStr) {
  if (!localStr) return "";
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

const EMPTY_PLAN = {
  key: "",
  label: "",
  amount_paise: 1900,
  amount_rupees: 19,
  // iter125 — limited-time offer fields
  original_price_rupees: 0,
  offer_badge: "",
  offer_label: "",
  offer_ends_at: "",
  currency: "INR",
  validity_days: 30,
  is_setup_fee: false,
  is_subscription: true,
  features: [],
  benefits: [],
  benefits_od: [],
  psychological_cta: "",
  time_saved_per_month: "",
  mind_benefit: "",
  health_benefit: "",
  description_en: "",
  description_od: "",
  sort_order: 10,
};

function PlanEditor({ initial, onSubmit, onCancel, isNew }) {
  const [form, setForm] = useState({ ...EMPTY_PLAN, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setRupees = (rupees) => {
    const num = Math.max(0, Number(rupees) || 0);
    setForm((p) => ({ ...p, amount_rupees: num, amount_paise: num * 100 }));
  };
  const listField = (field, label, placeholder) => (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">{label}</label>
      <textarea
        value={(form[field] || []).join("\n")}
        onChange={(e) => set(field, e.target.value.split("\n").filter((s) => s.trim().length))}
        rows={4}
        placeholder={placeholder}
        className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm leading-relaxed"
        data-testid={`plan-edit-${field}`}
      />
      <p className="mt-1 text-[0.7rem] text-ink-700">One bullet per line.</p>
    </div>
  );

  const submit = async () => {
    if (!form.label?.trim()) { toast.error("Label is required"); return; }
    if (isNew && !form.key?.trim()) { toast.error("Plan key is required for a new plan"); return; }
    setBusy(true);
    try {
      await onSubmit(form);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 rounded-2xl border-2 border-emerald-200 bg-white p-5 shadow-sm" data-testid="plan-editor">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">Key (stable ID)</label>
          <Input
            value={form.key}
            onChange={(e) => set("key", e.target.value.toLowerCase().replace(/\s/g, "_"))}
            disabled={!isNew}
            placeholder="e.g. mdcf_monthly"
            className="mt-1 font-mono text-sm"
            data-testid="plan-edit-key"
          />
          {!isNew && <p className="mt-1 text-[0.7rem] text-amber-700">Key cannot be changed once a plan is created.</p>}
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">Label</label>
          <Input value={form.label} onChange={(e) => set("label", e.target.value)} className="mt-1" data-testid="plan-edit-label" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">Price (₹)</label>
          <Input type="number" min={1} value={form.amount_rupees} onChange={(e) => setRupees(e.target.value)} className="mt-1 font-mono" data-testid="plan-edit-price" />
        </div>
        <div>
          <label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-rose-700">
            🔥 Original Price (₹) — strikethrough
          </label>
          <Input type="number" min={0} value={form.original_price_rupees || 0} onChange={(e) => set("original_price_rupees", Number(e.target.value) || 0)} placeholder="e.g. 99 (leave 0 for no offer)" className="mt-1 font-mono" data-testid="plan-edit-original-price" />
          <p className="mt-1 text-[0.7rem] text-ink-700">Set higher than current price to enable strikethrough offer.</p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">Validity (days)</label>
          <Input type="number" min={0} value={form.validity_days || 0} onChange={(e) => set("validity_days", Number(e.target.value) || 0)} className="mt-1 font-mono" data-testid="plan-edit-validity" />
          <p className="mt-1 text-[0.7rem] text-ink-700">Use 0 for one-time / setup fees.</p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">Sort order</label>
          <Input type="number" value={form.sort_order || 0} onChange={(e) => set("sort_order", Number(e.target.value) || 0)} className="mt-1 font-mono" data-testid="plan-edit-sort" />
        </div>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.is_subscription} onCheckedChange={(v) => set("is_subscription", v)} />
            <span>Subscription plan</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.is_setup_fee} onCheckedChange={(v) => set("is_setup_fee", v)} />
            <span>Setup fee (one-time)</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-ink-700"><Clock className="h-3 w-3" /> Time saved</label>
          <Input value={form.time_saved_per_month} onChange={(e) => set("time_saved_per_month", e.target.value)} placeholder="e.g. Saves 4-6 hours/month" className="mt-1 text-sm" data-testid="plan-edit-time" />
        </div>
        <div>
          <label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-ink-700"><Brain className="h-3 w-3" /> Mind benefit</label>
          <Input value={form.mind_benefit} onChange={(e) => set("mind_benefit", e.target.value)} placeholder="Stress / clarity copy" className="mt-1 text-sm" data-testid="plan-edit-mind" />
        </div>
        <div>
          <label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-ink-700"><Heart className="h-3 w-3" /> Health benefit</label>
          <Input value={form.health_benefit} onChange={(e) => set("health_benefit", e.target.value)} placeholder="Posture / strain copy" className="mt-1 text-sm" data-testid="plan-edit-health" />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">Psychological CTA</label>
        <Input value={form.psychological_cta} onChange={(e) => set("psychological_cta", e.target.value)} placeholder="One-liner emotional hook" className="mt-1" data-testid="plan-edit-cta" />
      </div>

      {/* iter125 — limited-time offer copy */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-rose-700">Offer badge (top-left ribbon)</label>
          <Input value={form.offer_badge || ""} onChange={(e) => set("offer_badge", e.target.value)} placeholder="e.g. 🔥 80% OFF" className="mt-1" data-testid="plan-edit-offer-badge" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-rose-700">Offer label (below price)</label>
          <Input value={form.offer_label || ""} onChange={(e) => set("offer_label", e.target.value)} placeholder="e.g. Limited-time website promotion" className="mt-1" data-testid="plan-edit-offer-label" />
        </div>
        <div>
          <label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-rose-700">
            ⏰ Offer ends at (countdown)
          </label>
          <Input
            type="datetime-local"
            value={isoToLocalInput(form.offer_ends_at)}
            onChange={(e) => set("offer_ends_at", localInputToIso(e.target.value))}
            className="mt-1 font-mono text-sm"
            data-testid="plan-edit-offer-ends-at"
          />
          <p className="mt-1 text-[0.7rem] text-ink-700">Leave blank to show a static label. Set a future date to enable a live "Ends in Xd Yh Zm" countdown.</p>
          {form.offer_ends_at && (
            <div className="mt-2"><CountdownPill endsAt={form.offer_ends_at} testId="plan-edit-offer-preview" /></div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">Description (English)</label>
          <textarea value={form.description_en} onChange={(e) => set("description_en", e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-700">Description (Odia)</label>
          <textarea value={form.description_od} onChange={(e) => set("description_od", e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" />
        </div>
        {listField("benefits", "Benefits — English", "Generate monthly MDCF instantly\nSaves 4-6 hours every month")}
        {listField("benefits_od", "Benefits — Odia", "ମାସିକ MDCF ତୁରନ୍ତ ତିଆରି\nପ୍ରତି ମାସ ୪-୬ ଘଣ୍ଟା ବଞ୍ଚାଯିବ")}
        {listField("features", "Feature flags (developer)", "mdm_bills\nmdcf_monthly_odia")}
      </div>

      <div className="flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="plan-edit-cancel">
          <X className="mr-2 h-4 w-4" /> Cancel
        </Button>
        <Button onClick={submit} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="plan-edit-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          {isNew ? "Create plan" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);     // existing key OR "__new"
  const [busyDel, setBusyDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/subscription-plans`);
      setPlans(r.data?.plans || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load plans");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createPlan = async (form) => {
    try {
      await axios.post(`${API}/admin/subscription-plans`, form);
      toast.success("Plan created.");
      setEditingKey(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const updatePlan = async (form) => {
    const { key, ...rest } = form;
    try {
      await axios.put(`${API}/admin/subscription-plans/${key}`, rest);
      toast.success(`Plan '${key}' updated.`);
      setEditingKey(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const removePlan = async (planKey) => {
    if (!window.confirm(`Delete plan '${planKey}'? This cannot be undone.`)) return;
    setBusyDel(planKey);
    try {
      await axios.delete(`${API}/admin/subscription-plans/${planKey}`);
      toast.success(`Plan '${planKey}' deleted.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusyDel(null); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-ink-700"><Loader2 className="h-4 w-4 animate-spin" /> Loading plans…</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-plans-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink-900">Subscription Plans</h2>
          <p className="mt-0.5 text-xs text-ink-700">Edit prices, benefits, features and Odia copy. Changes take effect immediately for new purchases.</p>
        </div>
        {!editingKey && (
          <Button onClick={() => setEditingKey("__new")} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="plan-add-btn">
            <Plus className="mr-2 h-4 w-4" /> Add new plan
          </Button>
        )}
      </div>

      {editingKey === "__new" && (
        <PlanEditor initial={EMPTY_PLAN} isNew onSubmit={createPlan} onCancel={() => setEditingKey(null)} />
      )}

      {plans.map((p) => (
        <div key={p.key} data-testid={`plan-card-${p.key}`}>
          {editingKey === p.key ? (
            <PlanEditor initial={p} onSubmit={updatePlan} onCancel={() => setEditingKey(null)} />
          ) : (
            <div className="rounded-2xl border border-border bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-ink-900">{p.label}</h3>
                    <code className="rounded bg-sand-100 px-1.5 py-0.5 text-[0.65rem] text-ink-700">{p.key}</code>
                    {p.is_subscription && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-emerald-800">Subscription</span>}
                    {p.is_setup_fee && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-amber-800">Setup Fee</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap items-baseline gap-3 text-sm">
                    <span className="inline-flex items-center gap-0.5 text-lg font-bold text-ink-900">
                      <IndianRupee className="h-4 w-4" />{p.amount_rupees}
                    </span>
                    {(p.original_price_rupees || 0) > (p.amount_rupees || 0) && (
                      <span className="inline-flex items-center text-sm font-semibold text-rose-600 line-through decoration-rose-500 decoration-2">
                        <IndianRupee className="h-3 w-3" />{p.original_price_rupees}
                      </span>
                    )}
                    {p.validity_days ? <span className="text-ink-700">/ {p.validity_days} days</span> : <span className="text-ink-700">one-time</span>}
                    {p.offer_badge && <span className="rounded-full bg-red-600 px-2 py-0.5 text-[0.6rem] font-bold text-white">{p.offer_badge}</span>}
                  </div>
                  {(p.offer_label || p.offer_ends_at) && (
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs font-semibold text-red-700">
                      {p.offer_label && <span>⏰ {p.offer_label}</span>}
                      <CountdownPill endsAt={p.offer_ends_at} testId={`plan-card-countdown-${p.key}`} />
                    </p>
                  )}
                  {p.psychological_cta && <p className="mt-1 text-sm italic text-emerald-700">"{p.psychological_cta}"</p>}
                  <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-ink-700 sm:grid-cols-3">
                    {p.time_saved_per_month && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-emerald-600" /> {p.time_saved_per_month}</span>}
                    {p.mind_benefit && <span className="inline-flex items-center gap-1"><Brain className="h-3 w-3 text-violet-600" /> {p.mind_benefit}</span>}
                    {p.health_benefit && <span className="inline-flex items-center gap-1"><Heart className="h-3 w-3 text-rose-600" /> {p.health_benefit}</span>}
                  </div>
                  <p className="mt-2 text-xs text-ink-700"><b>Features:</b> {(p.features || []).join(", ") || "—"}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditingKey(p.key)} data-testid={`plan-edit-btn-${p.key}`}>
                    <Edit3 className="mr-2 h-4 w-4" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyDel === p.key || p.key === "admin_setup"}
                    onClick={() => removePlan(p.key)}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    title={p.key === "admin_setup" ? "Setup-fee plan can't be deleted" : "Delete plan"}
                    data-testid={`plan-delete-btn-${p.key}`}
                  >
                    {busyDel === p.key ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
